import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  tap,
  switchMap,
  debounceTime,
  pipe,
  throwError,
  forkJoin,
  of,
  EMPTY,
} from 'rxjs';
import { LoopsApi } from '../../api/loops.api';
import { ClustersApi } from '../../api/clusters.api';
import { UsersApi } from '../../api/users.api';
import {
  PlaceAutocompleteOption,
  CreateLoopContract,
  CustomWaypointContract,
  LoopContract,
  UpdateLoopMapSettingsContract,
  VisitContract,
  VisitStatus,
  CustomWaypointStatus,
} from './types';
import { withRequestStatus } from '../../../global/store/features/request-status.feature';
import { AccountContract } from '../accounts/types';
import { BaseClusterContract } from '../clusters/types';
import { parseAccounts } from '../../utils/parse-accounts';
import { v4 } from 'uuid';
import {
  instanceOfVisit,
  instanceOfWaypoint,
} from '../../utils/instance-of-visit-or-waypoint';
import { AuthStore } from '../../../global/store/authentication/auth.store';
import {
  UserContract,
  hasOrgPermission,
  Permission,
} from '../../../global/store/authentication/types';

type LoopsState = {
  loops: LoopContract[] | undefined;
  loop: LoopContract | undefined;
  nearbyAccounts: AccountContract[] | undefined;
  suggestedVisit: VisitContract | undefined;
  warmVisit: VisitContract | undefined;
  autoCompleteSessionToken: string | undefined;
  autoCompleteOptions: PlaceAutocompleteOption[] | undefined;
  mapSearchResult: { latitude: number; longitude: number } | undefined;
  loopContext:
    | { cluster: BaseClusterContract; salesRep: UserContract | undefined }
    | undefined;
};

const initialState: LoopsState = {
  loops: undefined,
  loop: undefined,
  nearbyAccounts: undefined,
  suggestedVisit: undefined,
  warmVisit: undefined,
  autoCompleteSessionToken: undefined,
  autoCompleteOptions: undefined,
  mapSearchResult: undefined,
  loopContext: undefined,
};

export const LoopsStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialState),
  withRequestStatus([
    'getLoops',
    'getLoopByDate',
    'getSuggestedAccountToVisit',
    'createLoop',
    'addVisitToLoop',
    'deleteVisitFromLoop',
    'getNearbyAccounts',
    'confirmLoop',
    'createWarmVisit',
    'getCustomWaypointAutocompleteOptions',
    'addCustomWaypointToLoop',
    'deleteCustomWaypointFromLoop',
    'reorderLoop',
    'snoozeVisit',
    'addTimeToVisit',
    'addHomeVisitToLoop',
    'updateMapSettings',
    'getPlaceLatLng',
    'updateStartHome',
    'updateEndHome',
    'clearLoopById',
    'optimizeRoute',
    'setLoopContext',
  ]),
  withMethods(
    (
      store,
      loopsApi = inject(LoopsApi),
      clustersApi = inject(ClustersApi),
      usersApi = inject(UsersApi),
      authStore = inject(AuthStore),
    ) => ({
      getLoops: rxMethod<{
        startDate: Date;
        endDate: Date;
        salesRepId?: string;
      }>(
        switchMap((args) => {
          patchState(store, { loops: undefined });
          return loopsApi
            .getLoops(args.startDate, args.endDate, args.salesRepId)
            .pipe(
              store.handleActionPipe('getLoops', undefined),
              tap((loops) => {
                patchState(store, { loops });
              }),
            );
        }),
      ),
      getLoopByDate: rxMethod<{ date: Date; salesRepId?: string }>(
        switchMap(({ date, salesRepId }) => {
          return loopsApi.getLoopByDate(date, salesRepId).pipe(
            store.handleActionPipe('getLoopByDate', undefined),
            tap((loop) => {
              if (!loop) {
                patchState(store, { loop: undefined });
              } else {
                patchState(store, { loop });
              }
            }),
          );
        }),
      ),
      getSuggestedAccountToVisit: rxMethod<{
        date: Date;
        rangeInM: number;
        loopId?: string;
      }>(
        switchMap(({ date, rangeInM, loopId }) => {
          return loopsApi.getVisitSuggestion(date, rangeInM, loopId).pipe(
            store.handleActionPipe('getSuggestedAccountToVisit', undefined),
            tap((suggestedVisit) => {
              // Only update the suggested visit if it has an account
              if (suggestedVisit.account) {
                patchState(store, { suggestedVisit });
              }
            }),
          );
        }),
      ),
      createLoop: rxMethod<CreateLoopContract>(
        switchMap((createLoopContract) => {
          return loopsApi.createLoop(createLoopContract).pipe(
            store.handleActionPipe('createLoop', undefined),
            tap((loop) => {
              if (createLoopContract.customWaypoints) {
                patchState(store, {
                  loop,
                  autoCompleteSessionToken: undefined,
                });
              }
              patchState(store, { loop, suggestedVisit: undefined });
            }),
          );
        }),
      ),
      addVisitToLoop: rxMethod<{ loopId: string; visit: VisitContract }>(
        switchMap(({ loopId, visit }) => {
          return loopsApi.addVisitToLoop(loopId, visit).pipe(
            store.handleActionPipe('addVisitToLoop', undefined),
            tap((loop) => {
              patchState(store, { loop, suggestedVisit: undefined });
            }),
          );
        }),
      ),
      deleteVisitFromLoop: rxMethod<{ loopId: string; visitId: string }>(
        switchMap(({ loopId, visitId }) => {
          return loopsApi.deleteVisitFromLoop(loopId, visitId).pipe(
            store.handleActionPipe('deleteVisitFromLoop', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      getNearbyAccounts: rxMethod<{ loopId: string; rangeInM: number }>(
        switchMap(({ loopId, rangeInM }) => {
          return loopsApi.getNearbyAccounts(loopId, rangeInM).pipe(
            store.handleActionPipe('getNearbyAccounts', undefined),
            tap((nearbyAccounts) => {
              patchState(store, {
                nearbyAccounts: parseAccounts(nearbyAccounts),
              });
            }),
          );
        }),
      ),
      confirmLoop: rxMethod<LoopContract>(
        switchMap((loop) => {
          return loopsApi.confirmLoop(loop).pipe(
            store.handleActionPipe('confirmLoop', undefined),
            tap((loop) => {
              const loops = store.loops();
              if (loops) {
                const updatedLoops = [...loops];
                const loopIndex = updatedLoops.findIndex(
                  (l) => l.id === loop.id,
                );
                if (loopIndex !== -1) {
                  updatedLoops[loopIndex] = loop;
                  patchState(store, { loops: updatedLoops });
                }
              }
            }),
          );
        }),
      ),
      createWarmVisit: rxMethod<{ accountId: string; date: Date }>(
        switchMap(({ accountId, date }) => {
          const user = authStore.loggedInUser();
          return loopsApi
            .createWarmVisit(
              accountId,
              date,
              user?.centerPoint,
              user?.rangeInM,
              user?.startTime,
              user?.endTime,
              user?.averageVisitDurationInMinutes,
            )
            .pipe(
              store.handleActionPipe('createWarmVisit', undefined),
              tap((loop) => {
                patchState(store, { loop });
              }),
            );
        }),
      ),
      getCustomWaypointAutocompleteOptions: rxMethod<{
        input: string;
        loopId?: string;
      }>(
        pipe(
          debounceTime(300),
          switchMap(({ input, loopId }) => {
            if (!store.autoCompleteSessionToken()) {
              patchState(store, { autoCompleteSessionToken: v4() });
            }
            patchState(store, { autoCompleteOptions: undefined });
            return loopsApi
              .getCustomWaypointAutocompleteOptions(
                input,
                store.autoCompleteSessionToken()!,
                loopId,
              )
              .pipe(
                store.handleActionPipe(
                  'getCustomWaypointAutocompleteOptions',
                  undefined,
                ),
                tap((autoCompleteOptions) => {
                  patchState(store, { autoCompleteOptions });
                }),
              );
          }),
        ),
      ),
      addCustomWaypointToLoop: rxMethod<{
        loopId: string;
        placeId: string;
        name: string;
      }>(
        switchMap(({ loopId, placeId, name }) => {
          const sessionToken = store.autoCompleteSessionToken();
          if (!sessionToken) {
            // This should never happen
            return throwError(() => new Error('No session token found'));
          }
          return loopsApi
            .addCustomWaypointToLoop(loopId, placeId, name, sessionToken)
            .pipe(
              store.handleActionPipe('addCustomWaypointToLoop', undefined),
              tap((loop) => {
                patchState(store, {
                  loop,
                  autoCompleteSessionToken: undefined,
                });
              }),
            );
        }),
      ),
      getPlaceLatLng: rxMethod<string>(
        switchMap((placeId) => {
          const sessionToken = store.autoCompleteSessionToken();
          if (!sessionToken) {
            // This should never happen
            return throwError(() => new Error('No session token found'));
          }
          return loopsApi.getPlaceLatLng(placeId).pipe(
            store.handleActionPipe('getPlaceLatLng', undefined),
            tap((place) => {
              patchState(store, {
                mapSearchResult: place,
                autoCompleteSessionToken: undefined,
              });
            }),
          );
        }),
      ),
      addHomeVisitToLoop: rxMethod<{
        loopId: string;
        placeId: string;
        name: string;
      }>(
        switchMap(({ loopId, placeId, name }) => {
          return loopsApi.addHomeVisitToLoop(loopId, placeId, name).pipe(
            store.handleActionPipe('addHomeVisitToLoop', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      deleteCustomWaypointFromLoop: rxMethod<{
        loopId: string;
        customWaypointId: string;
      }>(
        switchMap(({ loopId, customWaypointId }) => {
          return loopsApi
            .deleteCustomWaypointFromLoop(loopId, customWaypointId)
            .pipe(
              store.handleActionPipe('deleteCustomWaypointFromLoop', undefined),
              tap((loop) => {
                patchState(store, { loop });
              }),
            );
        }),
      ),
      addTimeToVisit: rxMethod<{
        time: Date;
        loopId: string;
        visitId: string;
      }>(
        switchMap(({ time, loopId, visitId }) => {
          return loopsApi.addTimeToVisit(time, loopId, visitId).pipe(
            store.handleActionPipe('addTimeToVisit', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      reorderLoop: rxMethod<{
        loopId: string;
        visits: VisitContract[];
        customWaypoints: CustomWaypointContract[];
      }>(
        switchMap(({ loopId, visits, customWaypoints }) => {
          return loopsApi.reorderLoop(loopId, visits, customWaypoints).pipe(
            store.handleActionPipe('reorderLoop', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      snoozeVisit: rxMethod<{
        visitId: string;
        date: Date;
      }>(
        switchMap(({ visitId, date }) => {
          date.setHours(12, 0, 0, 0);
          return loopsApi.snoozeVisit(visitId, date).pipe(
            store.handleActionPipe('snoozeVisit', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      updateMapSettings: rxMethod<
        UpdateLoopMapSettingsContract & { loopId: string }
      >(
        switchMap(({ loopId, ...contract }) => {
          return loopsApi.updateMapSettings(contract, loopId).pipe(
            store.handleActionPipe('updateMapSettings', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      updateStartHome: rxMethod<{ loopId: string; startHome: boolean }>(
        switchMap(({ loopId, startHome }) => {
          return loopsApi.updateStartHome(loopId, startHome).pipe(
            store.handleActionPipe('updateStartHome', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      updateEndHome: rxMethod<{ loopId: string; endHome: boolean }>(
        switchMap(({ loopId, endHome }) => {
          return loopsApi.updateEndHome(loopId, endHome).pipe(
            store.handleActionPipe('updateEndHome', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      clearLoopById: rxMethod<string>(
        switchMap((loopId) => {
          return loopsApi.clearLoopById(loopId).pipe(
            store.handleActionPipe('clearLoopById', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      optimizeRoute: rxMethod<string>(
        switchMap((loopId) => {
          return loopsApi.optimizeRoute(loopId).pipe(
            store.handleActionPipe('optimizeRoute', undefined),
            tap((loop) => {
              patchState(store, { loop });
            }),
          );
        }),
      ),
      setLoopVisits: (inputLoop: LoopContract) => {
        const loops = store.loops();

        if (!loops) {
          return;
        }

        let visits = [...inputLoop.visits];

        patchState(store, {
          loops: loops.map((loop) =>
            loop.id === inputLoop.id ? { ...loop, visits } : loop,
          ),
        });
      },
      setLoopWaypoints: (inputLoop: LoopContract) => {
        const loops = store.loops();

        if (!loops) {
          return;
        }

        let customWaypoints = [...inputLoop.customWaypoints];

        patchState(store, {
          loops: loops.map((loop) =>
            loop.id === inputLoop.id ? { ...loop, customWaypoints } : loop,
          ),
        });
      },
      setWaypointOrder: (
        loopId: string,
        waypoints: (VisitContract | CustomWaypointContract)[],
      ) => {
        const loops = store.loops();
        if (!loops) {
          return;
        }

        const loop = loops.find((l) => l.id === loopId);

        if (!loop) {
          return;
        }

        let visits = [...loop.visits];
        let customWaypoints = [...loop.customWaypoints];

        for (const [index, waypoint] of waypoints.entries()) {
          if (instanceOfVisit(waypoint)) {
            const visitIndex = visits.findIndex(
              (visit) => visit.id === waypoint.id,
            );
            if (visitIndex !== -1) {
              visits[visitIndex] = { ...waypoint, index };
            }
            continue;
          }
          if (instanceOfWaypoint(waypoint)) {
            const waypointIndex = customWaypoints.findIndex(
              (w) => w.id === waypoint.id,
            );
            if (waypointIndex !== -1) {
              customWaypoints[waypointIndex] = {
                ...waypoint,
                index,
              };
            }
            continue;
          }
        }
        patchState(store, {
          loops: loops.map((loop) =>
            loop.id === loopId ? { ...loop, visits, customWaypoints } : loop,
          ),
        });
      },
      setLoops: (loops: LoopContract[]) => {
        patchState(store, { loops });
      },
      setVisits: (loopId: string, visits: VisitContract[]) => {
        const loops = store.loops();
        if (!loops) {
          return;
        }

        const loop = loops.find((l) => l.id === loopId);

        if (!loop) {
          return;
        }

        patchState(store, {
          loops: loops.map((loop) =>
            loop.id === loopId ? { ...loop, visits } : loop,
          ),
        });
      },
      setVisitStatus: (
        loopId: string,
        visitId: string,
        status: VisitStatus,
      ) => {
        const loops = store.loops();
        if (!loops) {
          return;
        }

        const loop = loops.find((l) => l.id === loopId);
        if (!loop) {
          return;
        }

        const visitIndex = loop.visits.findIndex(
          (visit) => visit.id === visitId,
        );

        if (visitIndex === -1) {
          return;
        }

        let visits = [...loop.visits];

        const existingVisit = visits[visitIndex];

        // If visit has no id and the new status is "deleted" or undefined, remove it
        if (
          (!existingVisit.id || existingVisit.id === '') &&
          (!status || status === 'deleted')
        ) {
          visits.splice(visitIndex, 1);
        } else {
          visits[visitIndex] = { ...existingVisit, status };
        }

        patchState(store, {
          loops: loops.map((loop) =>
            loop.id === loopId ? { ...loop, visits } : loop,
          ),
        });
      },
      setWaypointStatus: (
        loopId: string,
        waypointId: string,
        status: CustomWaypointStatus,
      ) => {
        const loops = store.loops();
        if (!loops) {
          return;
        }

        const loop = loops.find((l) => l.id === loopId);
        if (!loop) {
          return;
        }

        const waypointIndex = loop.customWaypoints.findIndex(
          (waypoint) => waypoint.id === waypointId,
        );

        if (waypointIndex === -1) {
          return;
        }

        let customWaypoints = [...loop.customWaypoints];

        const existingWaypoint = customWaypoints[waypointIndex];

        // If visit has no id and the new status is "deleted" or undefined, remove it
        if (
          (!existingWaypoint.id || existingWaypoint.id === '') &&
          (!status || status === 'deleted')
        ) {
          customWaypoints.splice(waypointIndex, 1);
        } else {
          customWaypoints[waypointIndex] = { ...existingWaypoint, status };
        }

        patchState(store, {
          loops: loops.map((loop) =>
            loop.id === loopId ? { ...loop, customWaypoints } : loop,
          ),
        });
      },
      setLoopContext: rxMethod<{
        clusterId: string | undefined;
        salesRepId: string | undefined;
      }>(
        switchMap(({ clusterId, salesRepId }) => {
          if (!clusterId) {
            patchState(store, { loopContext: undefined });
            return EMPTY;
          }
          const loggedInUser = authStore.loggedInUser();
          if (!loggedInUser) return EMPTY;

          let cluster$;
          if (hasOrgPermission(loggedInUser, Permission.CLUSTER_READ)) {
            cluster$ = clustersApi.getCluster(
              loggedInUser.salesOrganizationId,
              clusterId,
            );
          } else {
            const roleAssignment = loggedInUser.roleAssignments.find(
              (ra) => ra.clusterId === clusterId,
            );
            cluster$ = of({
              id: clusterId,
              name: roleAssignment?.clusterName ?? clusterId,
            });
          }
          const salesRep$ = salesRepId
            ? salesRepId === loggedInUser.id
              ? of(loggedInUser)
              : usersApi.getUserById(salesRepId)
            : of(undefined);

          return forkJoin([cluster$, salesRep$]).pipe(
            store.handleActionPipe('setLoopContext', undefined),
            tap(([cluster, salesRep]) => {
              patchState(store, {
                loopContext: { cluster, salesRep },
              });
            }),
          );
        }),
      ),
    }),
  ),
);
